/* eslint-disable camelcase */
import { Button, Col, Grid, Row, Spinner } from '@rocketmakers/armstrong';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  DeviceType,
  HeatingControlHeatPump,
  HeatPumpHeatingControl,
  RoomDialWrf06,
  RoomSensorMastertherm,
  RuleActionType,
  RuleCategory,
} from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { BackButton } from '../../components/buttons/back-button';
import { ConfirmAction } from '../../components/confirm-action/confirm-action';
import { DeviceSummary } from '../../components/layout/deviceSummary/device-summary';
import { useBeeConfig } from '../../hooks/beeConfig';
import { useZones } from '../../hooks/zones';
import { routePaths } from '../../routes';
import './add-boiler-heating-zones.scss';
import { getDeviceWithZoneDescription } from './utils';

const heatPumpHeatingControlMapping = Object.values(HeatPumpHeatingControl).reduce((acc, curr, i) => {
  acc[curr] = `Heat pump zone ${i + 1}`;
  return acc;
}, {});

export const AddHeatPumpHeatingZonesView: React.FunctionComponent = () => {
  const { heatPumpType } = useParams<{ heatPumpType: string }>();
  const { pushDevicesToConfig, getDevicesByType, devices, loading } = useBeeConfig();
  const { push } = useRoutePaths();
  const { getZoneDescription } = useZones();
  const [actionConfirmed, setActionConfirmed] = React.useState(false);

  type HeatPump = HeatingControlHeatPump | RoomSensorMastertherm;

  const heatingZoneDevices = React.useMemo(() => {
    const heatPumpDevices = getDevicesByType<HeatingControlHeatPump | RoomSensorMastertherm | RoomDialWrf06>([
      DeviceType.MASTERTHERM,
      DeviceType.WRF06,
    ]);
    return Object.entries(
      heatPumpDevices.reduce((zones, device) => {
        const {
          deviceType,
          params: { zone_id: zoneID, action_type: actionType },
        } = device;

        if (!zoneID) return zones;
        if (deviceType === DeviceType.WRF06 && actionType === RuleActionType.MONITORING) return zones;

        const deviceDescription =
          device.deviceType === DeviceType.MASTERTHERM
            ? heatPumpHeatingControlMapping[(device as HeatPump).params.heat_pump_heating_control]
            : getDeviceWithZoneDescription(device as RoomDialWrf06, getZoneDescription);

        return { ...zones, [zoneID]: { ...zones[zoneID], [device.deviceType]: deviceDescription } };
      }, {})
    );
  }, [devices]);

  const heatPumps = getDevicesByType<HeatingControlHeatPump>([DeviceType.MASTERTHERM])?.filter(
    ({ params: { category, action_type } }) =>
      category === RuleCategory.HEATING && action_type === RuleActionType.CONTROL
  );

  const handleAddHeatingZone = () => {
    push(routePaths.heatingSystem.assignHeatPumpHeatingZones.configure, { heatPumpType });
  };

  const handleNext = async () => {
    await pushDevicesToConfig();

    push(routePaths.hotWater.root);
  };

  return (
    <div>
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Heat Pump Heating Zones</span>
      </h1>
      <Grid className="grid">
        <Row>
          {heatingZoneDevices.map(([zoneID, descriptions]) => (
            <DeviceSummary
              key={zoneID}
              name={getZoneDescription(zoneID)}
              fields={[
                { name: 'Temp Dial:', value: descriptions[DeviceType.WRF06] },
                { name: 'Temp Sensor:', value: descriptions[DeviceType.MASTERTHERM] },
                { name: 'Control:', value: descriptions[DeviceType.MASTERTHERM] },
              ]}
              data-qa={`heating-zone-summary-${zoneID}`}
              onSelect={() =>
                push(routePaths.heatingSystem.assignHeatPumpHeatingZones.edit, { heatingZone: zoneID, heatPumpType })
              }
            />
          ))}
        </Row>
      </Grid>
      <Row>
        <ConfirmAction
          actionConfirmed={actionConfirmed}
          setActionConfirmed={() => setActionConfirmed(!actionConfirmed)}
          message="I confirm I've fixed the heat pump IP address in the pGDX controller to 192.168.0.120"
        />
      </Row>
      <Row className="btn-row">
        <Col>
          <Button
            className="btn-full"
            onClick={handleAddHeatingZone}
            data-qa="add-button-heating-zone"
            disabled={heatPumps?.length >= 2}
          >
            Add a heating zone
          </Button>
        </Col>
      </Row>
      <Row className="btn-row">
        <Button
          className="btn-full"
          onClick={handleNext}
          data-qa="continue-button"
          disabled={heatPumps?.length === 0 || !actionConfirmed}
        >
          Next
        </Button>
      </Row>
    </div>
  );
};
